/**
 * ChannelBarChart · Module B - 柱状图（按渠道维度）
 *
 * 需求 §4.4.5：
 * - X 轴渠道名（含 platform icon）
 * - Y 轴当前选中指标的数值
 * - 每条 series = 一款游戏（grouped bar），按选中游戏数量决定 series 数
 * - 配色 chartAllColors（EChartCommon 默认）
 * - tooltip / legend 复用 TrendCompare/utils（带游戏 icon）
 * - 渠道名优先使用 channelMap 映射，icon 走 `channelInfo`（@op/components/ChannelLabel）
 *
 * dataZoom（与 ChannelLineChart / TrendBarChart 保持一致）：
 * - X 轴渠道数 > dataZoomThreshold 时启用 inside + slider 双滑块
 * - 默认窗口 5 个渠道（与折线图阈值对齐），包含 N 个游戏的 grouped bar 时 5 个 X 点已较拥挤
 */
import React, { useEffect, useMemo, useRef } from 'react';
import EChartCommon from '@op/components/EChartCommon';
import { formatChartParamsDate } from '@op/components/ChartCommon/formatter';
import { DBO_EC, DBO_V } from '@op/utils/format';
import {
  ChannelIconType,
  channelInfo,
  channelLegendRichInfo,
  dealwithChannelChartKey,
} from '@op/components/ChannelLabel';
import { IChannelChartBarItem } from '../../types/channel';
import { TMetric } from '../../types/common';
import { TGameMap } from '../../hooks/useGameMap';
import { TChannelMap } from '../../hooks/useChannelMap';
import { useGameColorContext } from '../../context/GameColorContext';
import { METRIC_METAS } from '../../constants/metrics';
import {
  buildGameRich,
  gameRichLabel,
  gameTooltipLabel,
} from '../TrendCompare/utils';
import type { IGameSortValue } from './GameSortSelect';

export interface IChannelBarChartProps {
  /** 各渠道的游戏指标（每个 channel 有自己的 games[]） */
  list: IChannelChartBarItem[];
  gameMap: TGameMap;
  /** 渠道元信息映射（id → meta，用于渠道名称翻译） */
  channelMap: TChannelMap;
  metric: TMetric;
  height?: number;
  /** 触发 dataZoom 的窗口大小（X 轴渠道点数） */
  dataZoomThreshold?: number;
  emptyTitleText: string;
  loading?: boolean;
  /** 按指定游戏的指标值排序渠道 */
  sortByGame?: IGameSortValue;
  /** 图例顺序（游戏 ID 列表），仅控制 legend 显示顺序，不影响图表 series 顺序 */
  gameOrder?: string[];
}

const DEFAULT_DATA_ZOOM_THRESHOLD = 5;

export default function ChannelBarChart({
  list,
  gameMap,
  channelMap,
  metric,
  height = 360,
  dataZoomThreshold = DEFAULT_DATA_ZOOM_THRESHOLD,
  emptyTitleText,
  loading,
  sortByGame,
  gameOrder,
}: IChannelBarChartProps) {

  const { gameColorMap } = useGameColorContext();
  const chartWrapperRef = useRef<HTMLDivElement>(null);

  // 拦截滚轮事件：非 Shift 滚动让页面正常滚动，Shift+滚轮交给 ECharts 横向平移
  useEffect(() => {
    const el = chartWrapperRef.current;
    if (!el) return;

    const handleWheel = (e: WheelEvent) => {
      if (!e.shiftKey) {
        // 非 Shift 滚动：阻止 ECharts 捕获，让页面正常滚动
        e.stopPropagation();
      }
      // Shift 滚动：不做拦截，让 ECharts 处理横向平移
    };

    el.addEventListener('wheel', handleWheel, { capture: true, passive: false });
    return () => {
      el.removeEventListener('wheel', handleWheel, { capture: true });
    };
  }, []);

  const empty = useMemo(() => {
    return !list?.length || list.every((item) => !item.games?.length || item.games.every((g) => !g.value));
  }, [list]);

  const options = useMemo(() => {
    // ── 排序1：渠道排序（与 ChannelStackedBarChart 保持一致） ──
    //    - sortByGame.by 为非 '__total__' → 按指定游戏指标值排序（asc/desc）
    //    - sortByGame.by 为 '__total__' 或未传 → 按渠道总值排序（asc/desc）
    const isTotalSort = !sortByGame?.by || sortByGame.by === '__total__';
    const sortedList = [...list].sort((a, b) => {
      const getValue = (item: typeof list[number]) =>
        isTotalSort
          ? item.games.reduce((sum, g) => sum + (g.value || 0), 0)
          : (item.games.find((g) => g.game_id === sortByGame!.by)?.value || 0);

      const valA = getValue(a);
      const valB = getValue(b);
      return sortByGame?.order === 'asc' ? valA - valB : valB - valA;
    });

    // 1) X 轴：排序后的渠道 ID 列表
    const xAxisIds = sortedList.map((c) => c.channel_id);

    // 2) 收集所有出现过的 game_id（保证每条 series 对每个 channel 都有数据点，缺失补 0）
    const allGameIds = new Set<string>();
    list.forEach((c) => c.games.forEach((g) => allGameIds.add(g.game_id)));

    // ── 排序2：游戏排序 ──
    //    取排序1中排第一（总量最大）的渠道，按该渠道下各游戏的值降序排列
    const topChannel = sortedList[0];
    const gameValueInTopChannel = new Map<string, number>();
    if (topChannel) {
      topChannel.games.forEach((g) => {
        gameValueInTopChannel.set(g.game_id, g.value || 0);
      });
    }

    const gameIds = Array.from(allGameIds).sort((a, b) => {
      if (sortByGame?.by && !isTotalSort) {
        // 选中游戏始终排第一
        if (a === sortByGame.by) return -1;
        if (b === sortByGame.by) return 1;
      }
      return (gameValueInTopChannel.get(b) || 0) - (gameValueInTopChannel.get(a) || 0);
    });

    // 3) 构造 series：每个 game 一条柱（grouped），按排序后的渠道/游戏顺序填值
    //    通过 gameColorMap 统一设置图例颜色
    const echartSeries = gameIds.map((gameId) => {
      const data = sortedList.map((c) => {
        const found = c.games.find((g) => g.game_id === gameId);
        return found ? found.value : 0;
      });
      const color = gameColorMap[gameId];
      return {
        name: gameId, // ✅ 用 game_id 作为稳定 key，formatter 内部翻译
        type: 'bar' as const,
        data,
        // 全局统一颜色
        ...(color ? { itemStyle: { color } } : {}),
      };
    });

    const gameRich = buildGameRich(
      gameIds.map((id) => ({ game_id: id, values: [] })),
      gameMap,
      16,
    );

    // 4) X 轴 label 用渠道 icon（channelLegendRichInfo 已支持 marketing 渠道 + defaultImage）
    const channelRich = channelLegendRichInfo(20, 20, false, true);

    const yFormatter = METRIC_METAS[metric]?.yFormatter;
    const valueFormatter =
      yFormatter === 'percent'
        ? (val: number) => (Number.isFinite(val) ? DBO_V.percentageFormat(val * 100) : '-')
        : (val: number) => (Number.isFinite(val) ? DBO_V.reviews(val) : '-');

    const isMoreData = xAxisIds.length > dataZoomThreshold;

    // 图例顺序：按第一条渠道（sortedList[0]）的游戏值从大到小排列
    const firstChannelValues = new Map<string, number>();
    if (topChannel) {
      topChannel.games.forEach((g) => {
        firstChannelValues.set(g.game_id, g.value || 0);
      });
    }
    const legendData = (gameOrder?.length
      ? [...gameOrder].filter((id) => allGameIds.has(id))
      : Array.from(allGameIds)
    ).sort((a, b) => (firstChannelValues.get(b) || 0) - (firstChannelValues.get(a) || 0));

    const baseOptions: any = {
      grid: { containLabel: true },
      legend: {
        data: legendData,
        formatter: (gameId: string) => gameRichLabel(gameId, gameMap),
        textStyle: { rich: gameRich },
      },
      tooltip: {
        className: 'dbom-ad-comparison-channel-bar-chart-tooltip',
        formatter: (params: any) => {
          const channelId = params?.[0]?.axisValue as ChannelIconType;
          const { img } = channelInfo(channelId, undefined, true);
          const chName = channelMap[channelId]?.text
            || channelInfo(channelId, undefined, true).name
            || channelId;
          const iconHtml = img
            ? `<img src="${img}" class="dbom-channel-icon" />`
            : '';
          return formatChartParamsDate({
            title: `${iconHtml}${chName}`,
            params,
            isSort: true,
            listTitleFunc: (item: any) =>
              gameTooltipLabel(item.seriesName, gameMap),
            valueFormat: valueFormatter,
          });
        },
      },
      xAxis: {
        data: xAxisIds,
        axisLabel: {
          interval: 0,
          // rotate: xAxisIds.length > 8 ? 30 : 0,
          formatter: (channelId: string) => {
            const richKey = dealwithChannelChartKey(channelId);
            const iconKey = channelRich[richKey] ? richKey : dealwithChannelChartKey('default');
            const chName = channelMap[channelId]?.text
              || channelInfo(channelId as ChannelIconType, undefined, true).name
              || channelId;
            return `{${iconKey}|}{name|${chName}}`;
          },
          rich: {
            ...channelRich,
            name: {
              color: '#838697',
              fontSize: 12,
              padding: [0, 0, 0, 6],
              align: 'center',
            },
          },
        },
      },
      yAxis: {
        axisLabel: {
          formatter: (value: number) =>
            yFormatter === 'percent' ? DBO_EC.percent(value, false) : DBO_EC.reviews(value),
        },
      },
      series: echartSeries,
    };

    if (isMoreData) {
      const startIdx = 0;
      const endIdx = Math.min(dataZoomThreshold - 1, xAxisIds.length - 1);
      baseOptions.dataZoom = [
        {
          type: 'inside',
          xAxisIndex: 0,
          filterMode: 'none',
          startValue: startIdx,
          endValue: endIdx,
          zoomOnMouseWheel: false,
          moveOnMouseWheel: 'shift',
          moveOnMouseMove: false,
        },
        {
          type: 'slider',
          xAxisIndex: 0,
          filterMode: 'none',
          startValue: startIdx,
          endValue: endIdx,
          height: 16,
          bottom: 40,
          showDetail: false,
          showDataShadow: false,
          borderColor: 'rgba(0,0,0,0)',
          backgroundColor: 'rgba(0,0,0,0.02)',
          fillerColor: 'rgba(80, 134, 243, 0.2)',
          handleSize: '100%',
          handleStyle: { color: '#D8DAE4', borderCap: 'square' },
          moveHandleStyle: { color: '#D8DAE4' },
          emphasis: {
            handleStyle: { color: '#5086F3' },
            moveHandleStyle: { color: '#5086F3' },
          },
        },
      ];
      baseOptions.grid = { bottom: 70, containLabel: true };
    }

    return baseOptions;
  }, [list, dataZoomThreshold, gameMap, gameColorMap, metric, sortByGame, gameOrder]);

  return (
    <div ref={chartWrapperRef}>
      <EChartCommon
        options={options}
        optionsProps={{ type: 'bar', excludeOptions: ['axisPointer'] }}
        height={height}
        emptyTitleText={emptyTitleText}
        empty={empty}
        loading={loading}
        hasEmptyDesc={false}
      />
    </div>
  );
}
